"use client"
import React from "react";
import { FaTimes } from "react-icons/fa";
import SeatMapViewport from "./SeatMapViewport";
import { currencyFormatter } from "../../../helpers/Helper";
import TicketQuantityPicker from "./TicketQuantityPicker";
import api from "../../../helpers/Api";

export default function SeatPickerModal({ open, eventId, onClose, activePackage, layout, maxPerPerson = 9, initialSelection, onConfirm, finalPrice }) {
    const [selectedSeatIds, setSelectedSeatIds] = React.useState(() => new Set());
    const [qtyByPackage, setQtyByPackage] = React.useState(() => ({}));
    const packageId = String(activePackage?.event_package_id ?? "");
    const [soldSeatIds, setSoldSeatIds] = React.useState([]);
    const [heldSeatIds, setHeldSeatIds] = React.useState([]);

    React.useEffect(() => {
        if (!open) return;
        if (!eventId) return;

        const fetchAvailability = async () => {
            try {
                const res = await api.get(`/Event/SeatAvailability?eventId=${eventId}`);
                setSoldSeatIds(res.data.soldSeatIds || []);
                setHeldSeatIds(res.data.heldSeatIds || []);
            } catch (err) {
                console.error("Error fetching seat availability:", err);
                setSoldSeatIds([]);
                setHeldSeatIds([]);
            }
        };

        fetchAvailability();
    }, [open, eventId]);

    React.useEffect(() => {
        if (!open || !activePackage) return;

        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = "hidden";
        if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
        };
    }, [open]);

    React.useEffect(() => {
        if (!activePackage) return;

        if (activePackage.has_seats) {
            setQtyByPackage((p) => ({ ...p, [activePackage.event_package_id]: 0 }));
        } else {
            setSelectedSeatIds(new Set());
        }
    }, [activePackage?.event_package_id]);

    React.useEffect(() => {
        if (!open || !activePackage) return;

        const seatIds = initialSelection?.seatIds || [];
        const qty = initialSelection?.qty || 0;

        setSelectedSeatIds(new Set(seatIds));
        setQtyByPackage((p) => ({
            ...p,
            [String(activePackage?.event_package_id)]: qty,
        }));
    }, [open, initialSelection, activePackage?.event_package_id]);


    const onConfirmClick = () => {
        const seatIds = Array.from(selectedSeatIds);
        const qty = qtyByPackage[activePackage.event_package_id] ?? 0;

        onConfirm?.({
            packageId,
            seatIds: activePackage.has_seats ? seatIds : [],
            qty: activePackage.has_seats ? seatIds.length : qty,
        });
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-end md:items-center justify-center p-4">
            <div className=" w-full md:max-w-6xl max-h-[90dvh] flex flex-col rounded-t-2xl md:rounded-xl bg-[#16151c] border border-white/10 shadow-2xl overflow-hidden fixed md:static bottom-0 md:bottom-auto left-0 md:left-auto right-0 md:right-auto">
                <div className="flex-none flex items-center justify-between p-4 border-b border-white/10">
                    <div>
                        <h3 className="text-2xl font-bold text-white">Seleccionar asientos</h3>
                        <p>Categoría: {activePackage?.category || "—"} </p>
                        <p>Precio: {currencyFormatter(finalPrice) || "—"}</p>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-2 rounded hover:bg-white/10"
                        aria-label="Cerrar"
                    >
                        <FaTimes className="text-white" />
                    </button>
                </div>


                <div className="p-4 overflow-y-auto flex-1">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="text-white/80 text-sm">
                            Seleccionados: <span className="font-semibold text-white">{selectedSeatIds.size}</span>
                            <span className="text-white/60"> / {maxPerPerson}</span>
                        </div>

                        <button
                            type="button"
                            onClick={() => setSelectedSeatIds(new Set())}
                            className="text-xs px-3 py-1 rounded bg-white/10 hover:bg-white/15 text-white"
                        >
                            Limpiar
                        </button>
                    </div>

                    {activePackage?.has_seats ? (
                        <SeatMapViewport
                            layout={layout}
                            activePackageId={String(activePackage.event_package_id)}
                            selectedSeatIds={selectedSeatIds}
                            maxSelectable={maxPerPerson}
                            onToggleSeat={(seatId) => {
                                setSelectedSeatIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(seatId)) next.delete(seatId);
                                    else next.add(seatId);
                                    return next;
                                });
                            }}
                            selectedCount={selectedSeatIds.size}
                            soldSeatIds={soldSeatIds}
                            heldSeatIds={heldSeatIds}
                        />
                    ) : (
                        <TicketQuantityPicker
                            title={activePackage?.category}
                            max={maxPerPerson}
                            value={qtyByPackage[activePackage.event_package_id] ?? 0}
                            onChange={(n) =>
                                setQtyByPackage((p) => ({ ...p, [activePackage.event_package_id]: n }))
                            }
                        />
                    )}

                </div>

                <div className="flex-none p-4 border-t border-white/10 flex justify-end gap-3 bg-[#16151c]">
                    <button
                        onClick={onConfirmClick}
                        className="px-5 py-2.5 rounded bg-green-600 hover:bg-green-700 text-white font-semibold min-h-[44px]"
                    >
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
}
